import { call, put, all, takeLatest, takeEvery } from 'redux-saga/effects';
import * as Sentry from '@sentry/browser';

import { LOCATION_CHANGE, push } from 'utils/routerRedux';
import { getSubdomain } from 'utils/browser';
import api from 'utils/api';
import auth from 'utils/auth';
import {
  createErrorToast,
  clearAllToasts,
} from 'containers/ToastNotificationContainer/actions';
import {
  CREATE_TOAST,
  CLEAR_ALL_TOASTS,
} from 'containers/ToastNotificationContainer/constants';
import { triggerToastFromSaga } from 'features/toaster/ToastSagasAdapter';

import {
  GET_ACCOUNT_REQUEST,
  GET_PROFILE_REQUEST,
  UPDATE_PROFILE_REQUEST,
  CREATE_USER_SESSION,
  DESTROY_USER_SESSION,
  USER_LOGOUT_REQUEST,
} from './constants';
import {
  getAccountRequest,
  getAccountSuccess,
  getAccountError,
  getProfileSuccess,
  getProfileError,
  updateProfileSuccess,
  updateProfileError,
  destroyUserSession as destroyUserSessionAction,
  userLogoutSuccess,
  userLogoutError,
} from './actions';

// Individual exports for testing
export function* getAccount() {
  try {
    const response = yield call(api.getAccountV2);
    yield put(getAccountSuccess(response.account));
  } catch (err) {
    yield put(getAccountError(err));
  }
}

export function* getProfile() {
  try {
    const response = yield call(api.getProfile);
    yield put(getProfileSuccess(response));
  } catch (err) {
    yield put(getProfileError(err));
  }
}

export function* updateProfile({ payload }) {
  try {
    const response = yield call(api.updateProfile, { user: payload });
    yield put(updateProfileSuccess(response));
  } catch (err) {
    yield put(updateProfileError(err));
  }
}

export function* createUserSession({ user, token, nextPathname }) {
  auth.storeToken(token);

  yield put(getAccountRequest());
  yield put(clearAllToasts());
  yield put(push(nextPathname || '/'));

  const subdomain = getSubdomain();

  // Redundantly set also in App/index.js
  Sentry.setUser({ id: `${subdomain}_${user.id}` });
}

export function* destroyUserSession({ redirectUrl }) {
  auth.removeToken();
  Sentry.configureScope(scope => scope.setUser(null));
  const targetUrl = redirectUrl || `${window.location.origin}/login`;
  window.location.replace(targetUrl);
}

export function* logoutUser() {
  try {
    const response = yield call(api.logoutUser);
    yield put(userLogoutSuccess(response));
    yield put(destroyUserSessionAction(response?.redirectUrl));
  } catch (err) {
    yield put(userLogoutError(err));
    yield put(createErrorToast(err.message));
  }
}

export function* watcher() {
  yield all([
    takeLatest(GET_ACCOUNT_REQUEST, getAccount),
    takeLatest(GET_PROFILE_REQUEST, getProfile),
    takeLatest(UPDATE_PROFILE_REQUEST, updateProfile),

    takeLatest(CREATE_USER_SESSION, createUserSession),
    takeLatest(DESTROY_USER_SESSION, destroyUserSession),

    takeLatest(USER_LOGOUT_REQUEST, logoutUser),

    // adapt to the legacy redux-based toasts to the redux-free new toast stack.
    // see comment in `ToastSagasAdapter` to learn more.
    // can be removed once all toasts are sent through `useToasts`.
    takeEvery(CREATE_TOAST, triggerToastFromSaga),
    takeEvery(CLEAR_ALL_TOASTS, triggerToastFromSaga),
    // for legacy pushWithToast actions
    takeEvery(LOCATION_CHANGE, triggerToastFromSaga),
  ]);
}

export default [watcher];
